/**
 * 나 탭 · 이모 수첩 (시안 04 D2c, 명세 v1.3 2장). 나 탭 「이모 수첩 ›」(옛 '기억')에서 들어옵니다.
 *
 *   뒤로 + 「이모 수첩」 → 이모 아바타 40 + 안내 → 멤버 모두 아는 것(별채대화) → 그 멤버만 아는 것(1:1, 멤버별)
 *   → 적지 않는 것 고지 → 「수첩 모두 지우기」(확인 대화상자)
 *
 * 최신이 위. 고치기 · 지우기는 D2b 시트와 되돌리기 토스트(stores/memo.js)를 같이 씁니다.
 * 이모 안내 바로 아래 「내가 적어 두기」(시안 13 N1, C-31 · D75): 적기 시트를 열고, 적으면 목록을 다시 읽어 맨 위에 보입니다.
 * 100개를 넘으면 맨 위에 이모가 한 번 「오래된 건 좀 정리해도 될까?」 + [정리하기](오래된 순 목록). 자동 삭제 없음.
 */
import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { MEMBER_KEYS, getMember } from '@config/members/index.js';
import * as meApi from '../../api/me.js';
import { errorMessageOf } from '../../api/client.js';
import { TopBar } from '../../components/TopBar.jsx';
import { Avatar } from '../../components/Avatar.jsx';
import { Button } from '../../components/Button.jsx';
import { MemoEditSheet, MemoItem } from '../../components/memo/Memo.jsx';
import { useGoBack } from '../../hooks/useGoBack.js';
import { useMemoBook } from '../../stores/memo.js';
import { track } from '../../api/track.js';
import { useNoteSheet } from '../../stores/noteSheet.js';

export const TIDY_AT = 100;
const TIDY_SEEN_KEY = 'byeolil-memo-tidy-seen';

function tidySeen() {
    try {
        return window.localStorage.getItem(TIDY_SEEN_KEY) === '1';
    } catch {
        return false;
    }
}

function markTidySeen() {
    try {
        window.localStorage.setItem(TIDY_SEEN_KEY, '1');
    } catch {
        // 못 적어도 그만
    }
}

export const MEMO_NOT_RECORDED =
    '다른 사람의 신상(생일·나이·연락처·주소·건강) · 힘든 순간에 한 말 · 건강 상태와 진단 · 돈 액수';

export function MemoBookPage() {
    const goBack = useGoBack('/me');
    const [memories, setMemories] = useState({ status: 'loading', items: [], error: null });
    const [editing, setEditing] = useState(null);
    const [confirmClear, setConfirmClear] = useState(false);
    const [clearing, setClearing] = useState(false);
    const openNoteSheet = useNoteSheet((state) => state.open);
    const lastSaved = useNoteSheet((state) => state.lastSaved);
    // 정리하기: 오래된 순으로 펼친 목록
    const [tidying, setTidying] = useState(false);
    const [tidyShown] = useState(() => !tidySeen());

    // 수첩 연 비율 (명세 v1.3 10장, D44)
    useEffect(() => {
        track('memo_open', { from: 'me' });
    }, []);
    const hiddenIds = useMemoBook((state) => state.hiddenIds);

    useEffect(() => {
        const controller = new AbortController();
        meApi
            .fetchMemories({ signal: controller.signal })
            .then((response) => setMemories({ status: 'ready', items: response.memories, error: null }))
            .catch((error) => {
                if (!controller.signal.aborted) {
                    setMemories({ status: 'error', items: [], error: errorMessageOf(error, '수첩을 펴지 못했어요.') });
                }
            });
        return () => {
            controller.abort();
        };
        // 적기 시트로 적거나 고치면 다시 읽습니다
    }, [lastSaved?.at]);

    const visible = memories.items.filter((memory) => !hiddenIds.includes(memory.memoriesId));
    const showTidy = memories.status === 'ready' && visible.length > TIDY_AT && (tidyShown || tidying);
    const oldestFirst = useMemo(
        () => [...visible].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)),
        [visible]
    );
    const shared = visible.filter((memory) => memory.scope === 'room');
    const byMember = useMemo(
        () =>
            MEMBER_KEYS.map((memberKey) => ({
                memberKey,
                items: visible.filter((memory) => memory.scope === 'chat' && memory.memberKey === memberKey),
            })).filter((group) => group.items.length),
        [visible]
    );

    async function clearAll() {
        setClearing(true);
        try {
            await meApi.clearMemories();
            setMemories({ status: 'ready', items: [], error: null });
            setConfirmClear(false);
        } finally {
            setClearing(false);
        }
    }

    return (
        <>
            <TopBar title="이모 수첩" onBack={goBack} />
            <main className="flex flex-1 flex-col gap-6 overflow-y-auto px-4 pt-4 pb-10">
                <div className="flex items-start gap-3">
                    <Avatar memberKey="imo" size={40} />
                    <p className="m-0 font-serif text-[16px] leading-[27px]">
                        단골 얘기는 내가 여기 적어 둬. 알려 주고 싶은 건 직접 적어도 되고, 틀린 건 고쳐 주고, 싫은 건
                        지워도 돼.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={() => openNoteSheet({ entry: 'me_tab', key: `add-${Date.now()}` })}
                    className="flex h-12 w-full items-center justify-center gap-1.5 rounded-[14px] border border-dashed border-brand/45 bg-surface text-[15px] font-semibold text-brand"
                >
                    <Plus size={18} strokeWidth={1.8} aria-hidden="true" />
                    내가 적어 두기
                </button>

                {showTidy ? (
                    <div className="flex flex-col gap-2 rounded-card bg-surface px-4 py-3.5">
                        <div className="flex items-start gap-3">
                            <Avatar memberKey="imo" size={32} />
                            <p className="m-0 font-serif text-[15px] leading-6">오래된 건 좀 정리해도 될까?</p>
                        </div>
                        {tidying ? (
                            <ul className="m-0 list-none p-0">
                                {oldestFirst.slice(0, 30).map((memory) => (
                                    <MemoItem key={memory.memoriesId} memory={memory} onEdit={setEditing} />
                                ))}
                            </ul>
                        ) : (
                            <Button
                                variant="secondary"
                                size="md"
                                className="self-start"
                                onClick={() => {
                                    markTidySeen();
                                    setTidying(true);
                                }}
                            >
                                정리하기
                            </Button>
                        )}
                    </div>
                ) : null}

                {memories.status === 'error' ? (
                    <p role="alert" className="m-0 text-caption text-error">
                        {memories.error}
                    </p>
                ) : null}
                {memories.status === 'ready' && !visible.length ? (
                    <div className="flex items-start gap-3 rounded-card bg-surface px-4 py-3.5">
                        <Avatar memberKey="imo" size={32} />
                        <p className="m-0 font-serif text-[15px] leading-6">
                            아직 적어 둔 게 없어. 얘기 나누다 보면 하나씩 생길 겨.
                        </p>
                    </div>
                ) : null}

                {shared.length ? (
                    <section className="flex flex-col">
                        <h2 className="m-0 text-[15px] leading-6 font-bold">멤버 모두 아는 것</h2>
                        <span className="mb-1 text-[12px] leading-4 text-text-secondary">
                            별채대화에서 들은 얘기 · {shared.length}
                        </span>
                        <ul className="m-0 list-none p-0">
                            {shared.map((memory) => (
                                <MemoItem key={memory.memoriesId} memory={memory} onEdit={setEditing} />
                            ))}
                        </ul>
                    </section>
                ) : null}

                {byMember.length ? (
                    <section className="flex flex-col gap-3">
                        <div className="flex flex-col">
                            <h2 className="m-0 text-[15px] leading-6 font-bold">그 멤버만 아는 것</h2>
                            <span className="text-[12px] leading-4 text-text-secondary">
                                1:1에서 한 얘기 · 다른 멤버는 몰라요
                            </span>
                        </div>
                        {byMember.map((group) => (
                            <div key={group.memberKey} className="flex flex-col">
                                <span className="mb-1 flex items-center gap-2">
                                    <Avatar memberKey={group.memberKey} size={24} />
                                    <span className="text-[14px] font-semibold">
                                        {getMember(group.memberKey).name}만 아는 것
                                    </span>
                                    <span className="text-[12px] text-text-secondary">{group.items.length}</span>
                                </span>
                                <ul className="m-0 list-none p-0">
                                    {group.items.map((memory) => (
                                        <MemoItem key={memory.memoriesId} memory={memory} onEdit={setEditing} />
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </section>
                ) : null}

                <div className="flex flex-col gap-1 rounded-xl bg-surface-sunken px-4 py-3">
                    <span className="text-[13px] font-semibold">수첩에 적지 않는 것</span>
                    <span className="text-[13px] leading-5 text-text-secondary">{MEMO_NOT_RECORDED}</span>
                </div>

                {visible.length ? (
                    confirmClear ? (
                        <div
                            role="alertdialog"
                            aria-label="수첩 모두 지우기"
                            className="flex flex-col gap-3 rounded-card border border-hairline bg-surface p-4"
                        >
                            <p className="m-0 text-[15px] leading-6">
                                수첩을 모두 지울까요? 멤버들이 지금까지 들은 얘기를 잊어요.
                            </p>
                            <div className="flex gap-2">
                                <Button variant="secondary" size="md" onClick={() => setConfirmClear(false)}>
                                    취소
                                </Button>
                                <Button size="md" className="bg-error" busy={clearing} onClick={clearAll}>
                                    모두 지우기
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            onClick={() => setConfirmClear(true)}
                            className="h-12 rounded-card border border-hairline text-[15px] font-semibold text-error"
                        >
                            수첩 모두 지우기
                        </button>
                    )
                ) : null}
            </main>
            <MemoEditSheet memory={editing} onClose={() => setEditing(null)} />
        </>
    );
}
